import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { locationDisplayName } from '../constants/countries';
import {
  CURRENCY_DISPLAY_MAP,
  INDUSTRY_DISPLAY_MAP,
  STAGE_DISPLAY_MAP,
} from '../constants/display-mappings';
import { formatCompactAmount } from '../utils/formatCompactAmount';
import { isUnitedStatesCountry } from '../utils/investorPricingView';

type LisaInvestor = {
  id: string;
  name: string;
  email: string;
  companyName: string;
  basedIn: string;
  countries: string[];
  industries: string[];
  stages: string[];
  currency: string;
  investmentMin: number | null;
  investmentMax: number | null;
  hasAcceptedFeeAgreement: boolean;
  paysFee: boolean | null;
  profileCompleted: boolean;
  isTest: boolean;
};

type FeeChoice = 'own' | 'startup' | 'us' | 'pending';

type LisaResponse = {
  success?: boolean;
  error?: string;
  investors?: LisaInvestor[];
};

function labels(values: string[], map: Record<string, string>): string {
  if (values.length === 0) return 'Not set';
  return values.map(value => map[value] || value).join(', ');
}

function locationLabels(values: string[]): string {
  if (values.length === 0) return 'Not set';
  return values.map(locationDisplayName).join(', ');
}

function amountLabel(row: LisaInvestor): string {
  if (row.investmentMin === null || row.investmentMax === null || !row.currency) {
    return 'Not set';
  }
  const currency =
    CURRENCY_DISPLAY_MAP[row.currency as keyof typeof CURRENCY_DISPLAY_MAP] || row.currency;
  return `${formatCompactAmount(row.investmentMin)} – ${formatCompactAmount(row.investmentMax)} ${currency}`;
}

export function feeChoice(
  row: Pick<LisaInvestor, 'basedIn' | 'hasAcceptedFeeAgreement' | 'paysFee'>
): FeeChoice {
  if (isUnitedStatesCountry(row.basedIn)) return 'us';
  if (row.hasAcceptedFeeAgreement && row.paysFee === true) return 'own';
  if (row.hasAcceptedFeeAgreement && row.paysFee === false) return 'startup';
  return 'pending';
}

const FEE_LABEL: Record<FeeChoice, string> = {
  own: 'Pays from own funds',
  startup: 'From the startup',
  us: 'Not asked (US)',
  pending: 'Not agreed',
};

const FEE_CLASS: Record<FeeChoice, string> = {
  own: 'text-emerald-400',
  startup: 'text-amber-300',
  us: 'text-gray-400',
  pending: 'text-gray-500',
};

const thClass =
  'sticky top-0 z-20 bg-gray-800 px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap border-b border-gray-700';
const tdClass = 'px-3 py-2 text-xs text-gray-300 align-top border-b border-gray-700';

const AdminLisaPreferencesPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<LisaInvestor[]>([]);
  const [query, setQuery] = useState('');
  const [feeFilter, setFeeFilter] = useState<'all' | FeeChoice>('all');
  const [showTests, setShowTests] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const getInvestorLisaPreferences = httpsCallable(functions, 'getInvestorLisaPreferences');
        const result = await getInvestorLisaPreferences({});
        const data = result.data as LisaResponse;
        if (cancelled) return;
        if (!data?.success) {
          setError(data?.error || 'Could not load investor preferences.');
          return;
        }
        setRows(data.investors || []);
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Could not load investor preferences.';
          setError(
            message.includes('not-found') || message.includes('NOT_FOUND')
              ? 'Deploy getInvestorLisaPreferences to load live preferences.'
              : message
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter(row => {
      if (!showTests && row.isTest) return false;
      if (feeFilter !== 'all' && feeChoice(row) !== feeFilter) return false;
      if (!needle) return true;
      const haystack = [
        row.companyName,
        row.name,
        row.email,
        row.basedIn,
        locationLabels(row.countries),
        labels(row.industries, INDUSTRY_DISPLAY_MAP),
        labels(row.stages, STAGE_DISPLAY_MAP),
        amountLabel(row),
        FEE_LABEL[feeChoice(row)],
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [rows, query, feeFilter, showTests]);

  const counts = useMemo(() => {
    const real = rows.filter(row => !row.isTest);
    const tally = { own: 0, startup: 0, us: 0, pending: 0 };
    real.forEach(row => {
      tally[feeChoice(row)] += 1;
    });
    return { real: real.length, hidden: rows.length - real.length, ...tally };
  }, [rows]);

  return (
    <div className="min-h-screen bg-[#212121] flex flex-col items-center pt-8 pb-16">
      <div className="w-full max-w-[90rem] px-4 pt-4">
        <header className="mb-6 text-center">
          <h1 className="text-3xl font-semibold text-white">LISA preferences</h1>
          <p className="mt-1 text-gray-400">
            Location, industry, stage, and amount for each investor, plus the 2% fee choice.
          </p>
        </header>

        <div className="mb-6 flex justify-center">
          <button
            type="button"
            onClick={() => navigate('/admindashboard')}
            className="px-8 py-2.5 bg-[#333333] border border-[#f0f0f0] rounded-xl text-white text-sm font-medium hover:bg-[#444444] transition-all"
          >
            Back to dashboard
          </button>
        </div>

        {!loading && !error ? (
          <p className="mb-4 text-sm text-white">
            Real investors: <span className="text-[#9fd8d0]">{counts.real}</span>
            {' · '}
            Pays from own funds: <span className="text-[#9fd8d0]">{counts.own}</span>
            {' · '}
            From the startup: <span className="text-[#9fd8d0]">{counts.startup}</span>
            {' · '}
            Not agreed: <span className="text-[#9fd8d0]">{counts.pending}</span>
            {' · '}
            US, not asked: <span className="text-[#9fd8d0]">{counts.us}</span>
            {counts.hidden > 0 ? (
              <>
                {' · '}
                Hidden tests: <span className="text-[#9fd8d0]">{counts.hidden}</span>
              </>
            ) : null}
          </p>
        ) : null}

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input
            type="search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search company, location, industry…"
            className="w-full max-w-md rounded-lg border border-gray-700 bg-[#333333] px-3 py-2 text-sm text-white placeholder:text-gray-500"
          />
          <select
            value={feeFilter}
            onChange={event => setFeeFilter(event.target.value as 'all' | FeeChoice)}
            className="rounded-lg border border-gray-700 bg-[#333333] px-3 py-2 text-sm text-white"
          >
            <option value="all">All fee choices</option>
            <option value="own">Pays from own funds</option>
            <option value="startup">From the startup</option>
            <option value="pending">Not agreed</option>
            <option value="us">Not asked (US)</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={showTests}
              onChange={event => setShowTests(event.target.checked)}
            />
            Show test accounts
          </label>
        </div>

        {loading ? (
          <p className="text-gray-400">Loading investor preferences…</p>
        ) : error ? (
          <p className="text-amber-300">{error}</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-700">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className={thClass}>Investor</th>
                  <th className={thClass}>Based in</th>
                  <th className={thClass}>Location</th>
                  <th className={thClass}>Industry</th>
                  <th className={thClass}>Stage</th>
                  <th className={thClass}>Amount</th>
                  <th className={thClass}>2% fee</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td className={tdClass} colSpan={7}>
                      No investors match this view.
                    </td>
                  </tr>
                ) : (
                  visible.map(row => {
                    const fee = feeChoice(row);
                    return (
                      <tr key={row.id} className="bg-[#2b2b2b]">
                        <td className={`${tdClass} min-w-[12rem]`}>
                          <div className="text-white">{row.companyName || row.name || '—'}</div>
                          {row.companyName && row.name ? (
                            <div className="mt-0.5 text-gray-400">{row.name}</div>
                          ) : null}
                          <div className="mt-0.5 text-gray-500">{row.email}</div>
                          {!row.profileCompleted ? (
                            <div className="mt-0.5 text-amber-300">Profile not finished</div>
                          ) : null}
                          {row.isTest ? (
                            <div className="mt-0.5 text-gray-500">Test account</div>
                          ) : null}
                        </td>
                        <td className={`${tdClass} whitespace-nowrap`}>{row.basedIn || '—'}</td>
                        <td className={`${tdClass} min-w-[10rem] max-w-xs`}>
                          {locationLabels(row.countries)}
                        </td>
                        <td className={`${tdClass} min-w-[12rem] max-w-sm`}>
                          {labels(row.industries, INDUSTRY_DISPLAY_MAP)}
                        </td>
                        <td className={`${tdClass} min-w-[8rem]`}>
                          {labels(row.stages, STAGE_DISPLAY_MAP)}
                        </td>
                        <td className={`${tdClass} whitespace-nowrap`}>{amountLabel(row)}</td>
                        <td className={`${tdClass} whitespace-nowrap ${FEE_CLASS[fee]}`}>
                          {FEE_LABEL[fee]}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLisaPreferencesPage;
